(() => {
  if (window.WeRelieveCart) return;
  const originalFetch = window.fetch.bind(window);
  const root = window.Shopify?.routes?.root || '/';
  let tail = Promise.resolve();
  let refreshTask;
  let refreshAgain = false;
  let refreshOpen = false;
  let revision = 0;
  let pending = 0;
  const enqueue = (task) => {
    pending += 1;
    const result = tail.then(task).finally(() => { pending -= 1; });
    tail = result.catch(() => {});
    return result;
  };
  const request = async (path, body) => {
    const response = await originalFetch(root + path, body ? {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
    } : { cache: 'no-store' });
    const data = await response.json();
    if (!response.ok || data.errors) throw new Error(data.description || data.errors || 'Cart request failed');
    return data;
  };
  const sectionNames = (sections) => Object.keys(sections || {}).slice(0, 5);
  const readSections = async (names) => {
    const url = new URL(window.location.href);
    url.searchParams.delete('section_id');
    url.searchParams.set('sections', names.join(','));
    const response = await originalFetch(url, { cache: 'no-store' });
    if (!response.ok) throw new Error('Cart sections unavailable');
    return response.json();
  };
  const reconcile = async (cart, names) => {
    const settings = window.FreeGiftSettings;
    if (!settings?.giftVariantId || !Number.isFinite(Number(settings.thresholdPrice)) || Number(settings.thresholdPrice) <= 0) return { cart, changed: false };
    const gifts = cart.items.filter(item => Number(item.variant_id) === Number(settings.giftVariantId) && item.properties && typeof item.properties._free_gift !== 'undefined');
    const eligible = cart.total_price >= Number(settings.thresholdPrice);
    const updates = {};
    gifts.forEach((item, index) => {
      const quantity = eligible && index === 0 ? 1 : 0;
      if (item.quantity !== quantity) updates[item.key] = quantity;
    });
    if (!(eligible && !gifts.length) && !Object.keys(updates).length) return { cart, changed: false };
    document.getElementById('free-gift-loader')?.style.setProperty('display', 'flex');
    try {
      const sectionOptions = { sections: names, sections_url: window.location.pathname };
      if (eligible && !gifts.length) {
        const added = await request('cart/add.js', { items: [{ id: settings.giftVariantId, quantity: 1, properties: { _free_gift: true } }], ...sectionOptions });
        cart = await request('cart.js');
        cart.sections = added.sections;
      } else {
        cart = await request('cart/update.js', { updates, ...sectionOptions });
      }
      return { cart, changed: true };
    } finally {
      document.getElementById('free-gift-loader')?.style.setProperty('display', 'none');
    }
  };
  const render = (cart, open = false) => {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && cart.sections?.['cart-drawer']) drawer.renderContents(cart, { open });
    const items = document.querySelector('cart-items');
    if (items && typeof items.getSectionsToRender === 'function') {
      items.classList.toggle('is-empty', cart.item_count === 0);
      document.getElementById('main-cart-footer')?.classList.toggle('is-empty', cart.item_count === 0);
      items.getSectionsToRender().forEach(section => {
        const html = cart.sections?.[section.section];
        const element = document.getElementById(section.id);
        const source = html && new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
        if (element && source) (element.querySelector(section.selector) || element).innerHTML = source.innerHTML;
      });
    }
  };
  const currentSections = () => [...new Set(['cart-drawer', 'cart-icon-bubble', ...(document.querySelector('cart-items')?.getSectionsToRender?.().map(section => section.section) || [])])].slice(0, 5);
  const refresh = (force = false, open = false) => {
    if (refreshTask) {
      if (force) {
        refreshAgain = true;
        refreshOpen = refreshOpen || open;
      }
      return refreshTask;
    }
    refreshTask = enqueue(async () => {
      const names = currentSections();
      const result = await reconcile(await request('cart.js'), names);
      if (result.changed || force) {
        if (!result.cart.sections || names.some(name => !result.cart.sections[name])) result.cart.sections = await readSections(names);
        result.cart._wrCartRevision = ++revision;
        render(result.cart, open);
      }
      return result.cart;
    }).finally(() => {
      refreshTask = undefined;
      if (refreshAgain) {
        const openAgain = refreshOpen;
        refreshAgain = false;
        refreshOpen = false;
        return refresh(true, openAgain);
      }
    });
    return refreshTask;
  };
  const report = (error) => {
    console.error('Cart synchronization:', error);
    const element = document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
    if (element) element.textContent = window.cartStrings?.error || error.message;
  };
  window.WeRelieveCart = { refresh, whenIdle: () => tail, get revision() { return revision; } };
  window.handleFreeGiftLogic = () => refresh().catch(report);
  window.updateFullCartDrawer = () => refresh(true).catch(report);
  window.fetch = function (input, init) {
    let url;
    try { url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, window.location.href); } catch { return originalFetch(input, init); }
    const operation = url.origin === window.location.origin && url.pathname.match(/\/cart\/(add|change|update|clear)(?:\.js)?$/)?.[1];
    const method = (init?.method || input?.method || 'GET').toUpperCase();
    if (!operation || method !== 'POST') return originalFetch(input, init);
    return enqueue(async () => {
      const response = await originalFetch(input, init);
      if (!response.ok) return response;
      let data;
      try { data = await response.clone().json(); } catch { return response; }
      if (data.errors) return response;
      try {
        const suppliedSections = sectionNames(data.sections);
        const cartPage = document.querySelector('cart-items');
        const names = cartPage ? [...new Set([...suppliedSections, ...currentSections()])].slice(0, 5) : (suppliedSections.length ? suppliedSections : currentSections());
        const fullCart = typeof data.item_count === 'number' && Array.isArray(data.items);
        const result = await reconcile(fullCart ? data : await request('cart.js'), names);
        if (result.changed) data = fullCart ? { ...result.cart } : { ...data, sections: result.cart.sections };
        if (!data.sections || names.some(name => !data.sections[name])) data.sections = await readSections(names);
        data._wrCartRevision = ++revision;
        if (!suppliedSections.length || cartPage) render({ ...result.cart, sections: data.sections, _wrCartRevision: revision }, !suppliedSections.length && operation === 'add');
        const headers = new Headers(response.headers);
        headers.delete('content-length');
        return new Response(JSON.stringify(data), { status: response.status, statusText: response.statusText, headers });
      } catch (error) {
        report(error);
        return response;
      }
    });
  };
  const xhrOpen = XMLHttpRequest.prototype.open;
  const xhrSend = XMLHttpRequest.prototype.send;
  const xhrOperations = new WeakMap();
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    let operation;
    try {
      const parsed = new URL(url, window.location.href);
      if (parsed.origin === window.location.origin && String(method).toUpperCase() === 'POST') operation = parsed.pathname.match(/\/cart\/(add|change|update|clear)(?:\.js)?$/)?.[1];
    } catch {}
    xhrOperations.set(this, operation);
    return xhrOpen.call(this, method, url, ...rest);
  };
  XMLHttpRequest.prototype.send = function (...args) {
    const operation = xhrOperations.get(this);
    if (operation) this.addEventListener('load', () => {
      if (this.status >= 200 && this.status < 300) refresh(true, operation === 'add').catch(report);
    }, { once: true });
    return xhrSend.apply(this, args);
  };
  document.addEventListener('submit', event => {
    const button = event.submitter;
    if (button?.name !== 'checkout') return;
    document.querySelectorAll('cart-items, cart-drawer-items').forEach(items => items.flushPendingChange?.());
    if (!pending) return;
    event.preventDefault();
    const form = event.target;
    tail.then(() => requestAnimationFrame(() => {
      const currentButton = button.id ? document.getElementById(button.id) : button;
      const currentForm = form.id ? document.getElementById(form.id) : form;
      if (currentForm?.isConnected && currentButton?.isConnected && !currentButton.disabled) currentForm.requestSubmit(currentButton);
    })).catch(report);
  }, true);
  window.addEventListener('pageshow', event => { if (event.persisted) refresh(true).catch(report); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', window.handleFreeGiftLogic, { once: true });
  else window.handleFreeGiftLogic();
})();
